// Иерархия работ (владелец 2026-10-06): структура проекта любой вложенности, как структура
// в MS Project, — набирается с клавиатуры, без кнопки «+». Строка без детей — работа (она
// тут же появляется в справочнике и графике), строка с детьми — раздел.
// Клавиши: Enter — строка ниже, Tab / Shift+Tab — вложить / поднять, Alt+↑↓ — переставить,
// ↑↓ — ходить, Backspace на пустой строке — убрать, Ctrl+V — вставить список из Excel
// (отступ табуляцией = уровень); мышью — за номер строки в другое место или раздел.
// Сохраняется само через секунду после правки; даты считаются на сервере сразу.
//
// С вечера 2026-10-06 (владелец: «не понимаю, чем иерархия отличается от представления
// с графиком» → «слить») это не отдельный экран, а режим таблицы «Модели ГПР»: вся логика
// здесь — хук `useIerarhiya` и ячейки `IerImya` / `IerPolya`, а строки с датами и Гантом
// рисует `Plan.tsx`. Отдельный экран `Ierarhiya` оставлен тонкой обёрткой на случай отладки.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ask, confirmBox } from '../dialog';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { VyborSpiska } from '../Podskazki';
import { RASK_LABEL } from './Works';

type Uzel = Data<'/api/projects/{pid}/wbs', 'get'>['uzly'][number];
export type IerStroka = { klyuch: string; id: number | null; name: string; glubina: number; zadach: number;
  work_id: number | null; netronuta: boolean; raskatka: string | null; dlit: number | null;
  /** порядок этажей (логика монтажа) — столбец структуры (2026-10-16) */
  logika: string | null;
  /** имя, под которым строка сохранена: пока планер стирает и набирает заново, строка не уходит */
  imyaSohr: string;
  /** контур планирования раздела (2026-10-07): проставляется работам под ним */
  kontur: string | null };

let schetchik = 1;
const novyjKlyuch = () => 'n' + (schetchik++);
/** где лежат строки: проект и разбивка (null — основная) */
type Kuda = { pid: number; rid: number | null };
const klyuchK = (k: Kuda) => k.pid + ':' + (k.rid ?? 'osn');
const pustaya = (glubina: number): IerStroka => ({ klyuch: novyjKlyuch(), id: null, name: '', glubina,
  zadach: 0, work_id: null, netronuta: true, raskatka: null, dlit: null, logika: null, imyaSohr: '', kontur: null });

/** дерево с сервера → строки с глубиной */
function izDereva(uzly: Uzel[]): IerStroka[] {
  return uzly.map(u => ({ klyuch: String(u.id), id: u.id, name: u.name, glubina: u.glubina,
    zadach: u.zadach, work_id: u.work_id, netronuta: u.netronuta, raskatka: u.raskatka, dlit: u.dlit, logika: u.logika ?? null, imyaSohr: u.name, kontur: u.kontur }));
}

/** родитель строки — ближайшая выше с меньшей глубиной (любой список строк с глубиной) */
export function roditel(rows: { glubina: number }[], i: number): number {
  for (let j = i - 1; j >= 0; j--) if (rows[j].glubina < rows[i].glubina) return j;
  return -1;
}
/** конец поддерева строки (индекс первой строки не глубже её) */
export function konecPoddereva(rows: { glubina: number }[], i: number): number {
  let j = i + 1;
  while (j < rows.length && rows[j].glubina > rows[i].glubina) j++;
  return j;
}
export const estDeti = (rows: { glubina: number }[], i: number) => i + 1 < rows.length && rows[i + 1].glubina > rows[i].glubina;

/** номера 1, 1.1, 1.2.3 по глубинам */
function nomera(rows: IerStroka[]): string[] {
  const sch: number[] = [];
  return rows.map(r => {
    if (!r.name.trim()) return '';
    sch.length = r.glubina + 1;
    sch[r.glubina] = (sch[r.glubina] || 0) + 1;
    return sch.slice(0, r.glubina + 1).map(x => x || 1).join('.');
  });
}

export type Sost = '' | 'правка' | 'сохраняю' | 'сохранено' | 'ошибка';

/** Состояние и действия иерархии: строки, сохранение, клавиши, вставка, перетаскивание,
    правка дней и раскатки. `koren` — где искать поля строк для фокуса.
    `razbivka` — id своей разбивки (несколько разбивок, 2026-10-16); null — основная «Этапы»
    (запросы без параметра, как у старого фронта). */
export function useIerarhiya(ctx: ShellCtx, koren: React.RefObject<HTMLElement>, vklyuchena = true, razbivka: number | null = null) {
  const [rows, setRows] = useState<IerStroka[]>([]);
  const [svernuto, setSvernuto] = useState<Set<string>>(new Set());
  // версия структуры каждой разбивки (ответ сервера): шлём её с PUT, чтобы правка поверх
  // чужой (другое окно) не стёрла работы, — сервер отвечает 409 stale, строки перечитываются
  const revs = useRef(new Map<string, number>());
  const [rev, setRev] = useState<number | null>(null);
  const [osnovnaya, setOsnovnaya] = useState(true);
  // адрес строк — проект + разбивка: ответ (в т.ч. запоздалый, после смены проекта или разбивки)
  // применяется только если планер всё ещё там; иначе чужие строки легли бы в текущую таблицу
  const tek = useRef<Kuda>({ pid: ctx.pid, rid: razbivka });
  tek.current = { pid: ctx.pid, rid: razbivka };
  const tam = (k: Kuda) => tek.current.pid === k.pid && tek.current.rid === k.rid;
  const wbsUrl = (k: Kuda) => p('/api/projects/{pid}/wbs', { pid: k.pid }) + (k.rid != null ? '?razbivka=' + k.rid : '');
  // перетаскивание мышью: за номер строки тянем строку с поддеревом, бросаем между строками;
  // «после раздела» — первой строкой внутри него
  const tyanu = useRef<number | null>(null);
  const [kuda, setKuda] = useState<{ i: number; posle: boolean; glubina: number } | null>(null);
  const [sost, setSost] = useState<Sost>('');
  const [kontury, setKontury] = useState<string[]>([]);   // контуры из «Списков» — подсказки в поле раздела
  const [logiki, setLogiki] = useState<string[]>([]);     // порядки этажей (системные логики) — список в строке работы
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<{ name: string }[]>('get', p('/api/projects/{pid}/logiki', { pid: ctx.pid }));
      if (zhiv && r.ok) setLogiki(r.data.map(x => x.name));
    })();
    return () => { zhiv = false; };
  }, [ctx.pid]);
  const [msg, setMsg] = useState('');
  const can = ctx.canPlan;
  const izm = useRef(0);          // счётчик правок: ответ сервера не затирает то, что набрано позже
  const tajmer = useRef<number | null>(null);

  /** всегда одна пустая строка внизу — туда пишут следующую */
  const sPustoj = (rs: IerStroka[]) => {
    const posl = rs[rs.length - 1];
    if (posl && !posl.name.trim() && posl.id == null) return rs;
    return [...rs, pustaya(posl ? posl.glubina : 0)];
  };


  // ---- сохранение: целиком, через секунду после последней правки ----
  const loadRef = useRef<() => Promise<void>>(async () => undefined);
  /** `k` — проект и разбивка, где строки набраны (не текущие: планер мог переключиться, пока шла пауза) */
  const sohranit = useCallback(async (rs: IerStroka[], k: Kuda) => {
    const moya = izm.current;
    const stroki: { id: number | null; klyuch: string; roditel: string | null; name: string }[] = [];
    // строка с сохранённым именем, у которой поле сейчас пустое, — планер стирает, чтобы
    // набрать заново: отправляем прежнее имя, иначе сервер удалил бы работу из графика
    const polnye = rs.filter(r => r.name.trim() || (r.id && r.imyaSohr));
    polnye.forEach((r, i) => {
      const ri = roditel(polnye, i);
      stroki.push({ id: r.id, klyuch: r.klyuch, roditel: ri < 0 ? null : polnye[ri].klyuch, name: r.name.trim() || r.imyaSohr });
    });
    setSost('сохраняю');
    const r = await call<Data<'/api/projects/{pid}/wbs', 'put'>>('put', wbsUrl(k), { stroki, rev: revs.current.get(klyuchK(k)) ?? null });
    if (!r.ok) {
      setSost('ошибка'); setMsg(r.error.message); ctx.onError?.(r.error.message);
      // структуру поменяли в другом окне — перечитать, чтобы правка не стёрла чужие строки
      if (r.error.code === 'stale' && tam(k)) void loadRef.current();
      return;
    }
    revs.current.set(klyuchK(k), r.data.rev);
    const t: string[] = [];
    if (r.data.sozdano_rabot) t.push('работ в графике: +' + r.data.sozdano_rabot);
    if (r.data.pereimenovano) t.push('переименовано: ' + r.data.pereimenovano);
    if (r.data.otkrepleno.length) t.push('стали этапами, работы остались без этапа: ' + r.data.otkrepleno.join(', '));
    if (r.data.udaleno.length) t.push('убраны из графика: ' + r.data.udaleno.join(', '));
    if (t.length) setMsg(t.join(' · '));
    setSost('сохранено');
    ctx.onError?.('');
    if (r.data.sozdano_rabot || r.data.pereimenovano || r.data.otkrepleno.length || r.data.udaleno.length) ctx.onChanged?.();
    if (!tam(k)) return;   // планер уже в другом проекте или разбивке — те строки не трогаем
    setRev(r.data.rev);
    // ответ — те же строки в том же порядке: подхватываем id и число задач, имена не трогаем,
    // если планер успел напечатать дальше
    const svezhie = izDereva(r.data.uzly);
    setRows(cur => {
      const polnyeCur = cur.filter(x => x.name.trim() || (x.id && x.imyaSohr));
      if (polnyeCur.length !== svezhie.length) {
        // сервер вернул не те строки (работа с фактом стала разделом и ушла из разбивки и т.п.):
        // без незавершённых правок берём его вариант, иначе перечитаем после них
        if (izm.current === moya) return sPustoj(svezhie);
        window.setTimeout(() => { if (!tajmer.current && tam(k)) void loadRef.current(); }, 1200);
        return cur;
      }
      const poKlyuchu = new Map<string, IerStroka>();
      polnyeCur.forEach((x, i) => poKlyuchu.set(x.klyuch, svezhie[i]));
      return cur.map(x => {
        const n = poKlyuchu.get(x.klyuch);
        if (!n) return x;
        return { ...x, id: n.id, zadach: n.zadach, work_id: n.work_id, netronuta: n.netronuta, raskatka: n.raskatka, dlit: n.dlit, imyaSohr: n.name, kontur: n.kontur,
          name: izm.current === moya ? n.name : x.name, glubina: izm.current === moya ? n.glubina : x.glubina };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!vklyuchena) return;
    const seq = ++loadSeq.current;
    const k: Kuda = { pid: ctx.pid, rid: razbivka };
    const [r, w, sp] = await Promise.all([
      call<Data<'/api/projects/{pid}/wbs', 'get'>>('get', wbsUrl(k)),
      call<Data<'/api/projects/{pid}/works', 'get'>>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
    ]);
    if (seq !== loadSeq.current || !tam(k)) return;   // планер успел переключить разбивку или проект — ответ устарел
    if (!r.ok) { setMsg(r.error.message); ctx.onError?.('Структура работ не загрузилась: ' + r.error.message); setRows([]); return; }
    if (sp.ok) setKontury(sp.data.filter(x => x.kod === 'kontur').map(x => x.znachenie));
    revs.current.set(klyuchK(k), r.data.rev);
    setRev(r.data.rev);
    setOsnovnaya(r.data.osnovnaya);
    const rs = izDereva(r.data.uzly);
    // работы вне иерархии (владелец 2026-10-06: «Стояки К1 оказались без этапа» — строку убрали,
    // работа осталась в справочнике и пропала из виду) — показываем в конце строками первого
    // уровня и тут же закрепляем в структуре, чтобы планер перетащил их в нужный раздел.
    // В своей разбивке — работы, у которых здесь нет строки (2026-10-16)
    const vDereve = new Set(rs.map(x => x.work_id).filter((x): x is number => x != null));
    const vne = w.ok ? w.data.filter(x => !vDereve.has(x.id)).sort((a, b) => a.name.localeCompare(b.name, 'ru')) : [];
    vne.forEach(x => rs.push({ klyuch: 'w' + x.id, id: null, name: x.name, glubina: 0, zadach: x.zadach,
      work_id: x.id, netronuta: false, raskatka: x.raskatka, dlit: x.dlit, logika: x.logika ?? null, imyaSohr: '', kontur: null }));
    setRows(sPustoj(rs));
    if (vne.length && can) {
      setMsg('Работы вне иерархии добавлены в конец структуры: ' + vne.map(x => x.name).join(', ') + ' — перетащите в нужный этап');
      izm.current += 1;
      void sohranit(rs, k);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, vklyuchena, razbivka, ctx.reloadToken]);   // reloadToken — перечитать после «Справочников» (2026-10-17)
  loadRef.current = load;
  useEffect(() => { void load(); }, [load]);

  // несохранённая правка ждёт паузы вместе с разбивкой, в которой набрана
  const ozhid = useRef<{ rs: IerStroka[]; k: Kuda } | null>(null);
  const pravka = (f: (rs: IerStroka[]) => IerStroka[], srazu = false) => {
    izm.current += 1;
    const k: Kuda = { pid: ctx.pid, rid: razbivka };
    setRows(cur => {
      const n = sPustoj(f(cur));
      ozhid.current = { rs: n, k };
      if (tajmer.current) window.clearTimeout(tajmer.current);
      setSost('правка');
      tajmer.current = window.setTimeout(() => {
        tajmer.current = null;
        const o = ozhid.current;
        ozhid.current = null;
        if (o) void sohranit(o.rs, o.k);
      }, srazu ? 50 : 900);
      return n;
    });
  };
  // переключили разбивку (или ушли с экрана) — не ждать паузу, дописать правку в прежнюю
  useEffect(() => () => {
    if (tajmer.current) { window.clearTimeout(tajmer.current); tajmer.current = null; }
    const o = ozhid.current;
    ozhid.current = null;
    if (o) void sohranit(o.rs, o.k);
    setRows([]);
    setSvernuto(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [razbivka, ctx.pid]);

  // ---- видимость при свёрнутых разделах ----
  const vidim = useMemo(() => {
    const out: boolean[] = [];
    const stek: { glubina: number; svern: boolean }[] = [];
    rows.forEach(r => {
      while (stek.length && stek[stek.length - 1].glubina >= r.glubina) stek.pop();
      out.push(!stek.some(s => s.svern));
      stek.push({ glubina: r.glubina, svern: svernuto.has(r.klyuch) });
    });
    return out;
  }, [rows, svernuto]);
  const noms = useMemo(() => nomera(rows), [rows]);

  const fokus = (i: number) => {
    window.setTimeout(() => {
      const el = (koren.current || document).querySelector<HTMLInputElement>('input[data-i="' + i + '"]');
      el?.focus();
    }, 0);
  };
  const sosed = (i: number, shag: 1 | -1) => {
    let j = i + shag;
    while (j >= 0 && j < rows.length && !vidim[j]) j += shag;
    return j >= 0 && j < rows.length ? j : i;
  };
  const svernut = (klyuch: string) => setSvernuto(s => { const n = new Set(s); if (n.has(klyuch)) n.delete(klyuch); else n.add(klyuch); return n; });

  // ---- клавиши ----
  async function klavisha(ev: React.KeyboardEvent<HTMLInputElement>, i: number) {
    if (!can) return;
    const r = rows[i];
    if (ev.key === 'Enter') {
      ev.preventDefault();
      // у раздела — первая строка внутри; у работы — следующая на том же уровне
      const nov = pustaya(estDeti(rows, i) ? r.glubina + 1 : r.glubina);
      pravka(rs => [...rs.slice(0, i + 1), nov, ...rs.slice(i + 1)]);
      fokus(i + 1);
      return;
    }
    if (ev.key === 'Tab') {
      ev.preventDefault();
      if (ev.shiftKey) {
        if (r.glubina === 0) return;
        const kon = konecPoddereva(rows, i);
        pravka(rs => rs.map((x, j) => (j >= i && j < kon ? { ...x, glubina: x.glubina - 1 } : x)));
      } else {
        // новый родитель — ближайшая строка выше того же уровня (между ними — поддерево соседа)
        let rod = i - 1;
        while (rod >= 0 && rows[rod].glubina > r.glubina) rod--;
        if (rod < 0 || rows[rod].glubina !== r.glubina || !rows[rod].name.trim()) return;   // вложить не под кого
        // родитель — работа с фактом, ручными датами или связями: предупредить, что она
        // станет разделом. Нетронутая (только что набранный заголовок) убирается молча
        if (!estDeti(rows, rod) && rows[rod].work_id && !rows[rod].netronuta) {
          const ok = await confirmBox('«' + rows[rod].name + '» станет этапом: её задачи ('
            + rows[rod].zadach + ') уйдут из графика, работа останется в справочнике без этапа. Продолжить?');
          if (!ok) return;
        }
        const kon = konecPoddereva(rows, i);
        pravka(rs => rs.map((x, j) => (j >= i && j < kon ? { ...x, glubina: x.glubina + 1 } : x)));
      }
      fokus(i);
      return;
    }
    if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
      ev.preventDefault();
      const shag = ev.key === 'ArrowUp' ? -1 : 1;
      if (ev.altKey) {
        const kon = konecPoddereva(rows, i);
        if (shag === -1) {
          if (i === 0) return;
          let j = i - 1;
          while (j > 0 && rows[j].glubina > rows[i].glubina) j--;   // начало соседнего блока выше
          pravka(rs => [...rs.slice(0, j), ...rs.slice(i, kon), ...rs.slice(j, i), ...rs.slice(kon)]);
          fokus(j);
        } else {
          if (kon >= rows.length || !rows[kon].name.trim()) return;
          const kon2 = konecPoddereva(rows, kon);
          pravka(rs => [...rs.slice(0, i), ...rs.slice(kon, kon2), ...rs.slice(i, kon), ...rs.slice(kon2)]);
          fokus(i + (kon2 - kon));
        }
        return;
      }
      fokus(sosed(i, shag));
      return;
    }
    if ((ev.key === 'Backspace' || ev.key === 'Delete') && !r.name && rows.length > 1) {
      ev.preventDefault();
      // строка с работой: она уйдёт из графика с задачами (владелец 2026-10-07); нетронутую
      // убираем молча, у работы с фактом, ручными датами или связями — спрашиваем
      if (r.work_id && !r.netronuta) {
        const ok = await confirmBox('Убрать «' + (r.imyaSohr || r.name) + '» из графика? Уйдут её задачи (' + r.zadach
          + '), факт, ручные даты и связи. Отменить будет нельзя.');
        if (!ok) { pravka(rs => rs.map((x, j) => (j === i ? { ...x, name: x.imyaSohr } : x))); return; }
      }
      const kon = konecPoddereva(rows, i);
      // дети пустой строки поднимаются на уровень
      pravka(rs => rs.filter((_, j) => j !== i).map((x, j) => (j >= i && j < kon - 1 ? { ...x, glubina: Math.max(0, x.glubina - 1) } : x)));
      fokus(Math.max(0, sosed(i, -1)));
      return;
    }
    if (ev.key === 'Escape') { (ev.target as HTMLInputElement).blur(); }
  }

  /** вставка списка из Excel: строка — узел, отступ табуляцией (или двумя пробелами) — уровень */
  function vstavka(ev: React.ClipboardEvent<HTMLInputElement>, i: number) {
    const text = ev.clipboardData.getData('text/plain');
    if (!text.includes('\n')) return;   // одна строка — обычная вставка в поле
    ev.preventDefault();
    const baza = rows[i].glubina;
    const novye: IerStroka[] = [];
    text.split(/\r?\n/).forEach(line => {
      if (!line.trim()) return;
      const m = /^([\t ]*)/.exec(line)!;
      const otstup = m[1].split('\t').length - 1 + Math.floor(m[1].replace(/\t/g, '').length / 2);
      const name = line.trim().split('\t')[0].trim();
      if (name) novye.push({ ...pustaya(baza + otstup), name });
    });
    if (!novye.length) return;
    let pred = rows[i - 1]?.glubina ?? -1;
    novye.forEach(n => { n.glubina = Math.min(n.glubina, pred + 1); pred = n.glubina; });
    pravka(rs => (rs[i].name.trim() ? [...rs.slice(0, i + 1), ...novye, ...rs.slice(i + 1)]
      : [...rs.slice(0, i), ...novye, ...rs.slice(i + 1)]), true);
    setMsg('Вставлено строк: ' + novye.length);
  }

  // ---- перетаскивание мышью ----
  /** куда ляжет строка, если бросить над строкой j: до неё или после (с её поддеревом) */
  function celDropa(j: number, posle: boolean) {
    const r = rows[j];
    if (!posle) return { index: j, glubina: r.glubina };
    if (estDeti(rows, j) && !svernuto.has(r.klyuch)) return { index: j + 1, glubina: r.glubina + 1 };   // первой внутри раздела
    return { index: konecPoddereva(rows, j), glubina: r.glubina };
  }
  function dragStart(ev: React.DragEvent, i: number) {
    tyanu.current = i; ev.dataTransfer.effectAllowed = 'move'; ev.dataTransfer.setData('text/plain', rows[i].name);
  }
  function dragEnd() { tyanu.current = null; setKuda(null); }
  function dragOver(ev: React.DragEvent<HTMLTableRowElement>, j: number) {
    const i = tyanu.current;
    if (i == null) return;
    const kon = konecPoddereva(rows, i);
    if (j >= i && j < kon) { setKuda(null); return; }   // в своё же поддерево нельзя
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
    const rect = ev.currentTarget.getBoundingClientRect();
    const posle = ev.clientY > rect.top + rect.height / 2;
    const c = celDropa(j, posle);
    setKuda(k => (k && k.i === j && k.posle === posle ? k : { i: j, posle, glubina: c.glubina }));
  }
  function dragLeave(j: number) { setKuda(k => (k && k.i === j ? null : k)); }
  function drop(j: number) {
    const i = tyanu.current;
    tyanu.current = null;
    const k = kuda;
    setKuda(null);
    if (i == null || !k || k.i !== j) return;
    const kon = konecPoddereva(rows, i);
    if (j >= i && j < kon) return;
    const c = celDropa(j, k.posle);
    const blok = rows.slice(i, kon);
    const delta = c.glubina - rows[i].glubina;
    const ost = rows.filter((_, x) => x < i || x >= kon);
    const idx = c.index > i ? c.index - blok.length : c.index;
    const novye = blok.map(x => ({ ...x, glubina: x.glubina + delta }));
    pravka(() => [...ost.slice(0, idx), ...novye, ...ost.slice(idx)], true);
    setMsg('«' + rows[i].name + '» перенесена');
  }

  // ---- поля работы: раскатка и дни ----
  async function perechitatPolya() {
    const k = tek.current;
    const d = await call<Data<'/api/projects/{pid}/wbs', 'get'>>('get', wbsUrl(k));
    if (!d.ok || !tam(k)) return;
    const svezhie = new Map(d.data.uzly.map(u => [u.id, u]));
    setRows(cur => cur.map(x => (x.id && svezhie.has(x.id)
      ? { ...x, zadach: svezhie.get(x.id)!.zadach, raskatka: svezhie.get(x.id)!.raskatka, dlit: svezhie.get(x.id)!.dlit, logika: svezhie.get(x.id)!.logika ?? null } : x)));
  }
  /** раскатка работы — справа от строки; задачи пересоздаются сервером */
  async function smenitRaskatku(r: IerStroka, v: string) {
    if (!r.work_id || v === r.raskatka) return;
    const res = await call<{ sozdano_zadach?: number; udaleno_zadach?: number }>('patch', p('/api/works/{wid}', { wid: r.work_id }), { raskatka: v });
    if (!res.ok) { setMsg(res.error.message); ctx.onError?.(res.error.message); return; }
    setMsg('«' + r.name + '»: ' + (RASK_LABEL[v] || v) + (res.data.sozdano_zadach ? ' · задач +' + res.data.sozdano_zadach : '')
      + (res.data.udaleno_zadach ? ' · убрано ' + res.data.udaleno_zadach : ''));
    ctx.onSaved?.(); ctx.onChanged?.();
    await perechitatPolya();
  }
  /** порядок этажей (логика монтажа) — столбец структуры (владелец 2026-10-15: «в раскатке и
      порядке этажей весь прикол»); значения — системные логики проекта */
  async function smenitLogiku(r: IerStroka, v: string) {
    if (!r.work_id || v === (r.logika || '')) return;
    const res = await call('patch', p('/api/works/{wid}', { wid: r.work_id }), { logika_montazha: v || null });
    if (!res.ok) { setMsg(res.error.message); ctx.onError?.(res.error.message); return; }
    setMsg('«' + r.name + '»: порядок этажей — ' + (v || 'по умолчанию') + ' — даты пересчитаются после «Пересчитать»');
    setRows(cur => cur.map(x => (x.klyuch === r.klyuch ? { ...x, logika: v || null } : x)));
    ctx.onSaved?.(); ctx.onChanged?.();
  }
  /** длительность работы (справочник), дней */
  async function smenitDlit(r: IerStroka, txt: string) {
    if (!r.work_id) return;
    const v = txt.trim() ? +txt.replace(',', '.') : null;
    if (v != null && (isNaN(v) || v < 0)) { setMsg('Длительность — число дней'); return; }
    if (v === (r.dlit ?? null)) return;
    const res = await call('patch', p('/api/works/{wid}', { wid: r.work_id }), { dlitelnost: v });
    if (!res.ok) { setMsg(res.error.message); ctx.onError?.(res.error.message); return; }
    setMsg('«' + r.name + '»: ' + (v == null ? 'длительность очищена' : v + ' дн') + ' — даты пересчитаются после «Пересчитать»');
    setRows(cur => cur.map(x => (x.klyuch === r.klyuch ? { ...x, dlit: v } : x)));
    ctx.onSaved?.(); ctx.onChanged?.();
  }

  /** контур планирования раздела: всем работам под ним, в «Списки» и в кнопки фильтров (2026-10-07) */
  async function smenitKontur(r: IerStroka, v: string) {
    if (!r.id) return;
    const nov = v.trim() || null;
    if (nov === (r.kontur ?? null)) return;
    const res = await call<{ kontur: string | null; rabot: number }>('patch', p('/api/projects/{pid}/wbs/{nid}', { pid: ctx.pid, nid: r.id }), { kontur: nov });
    if (!res.ok) { setMsg(res.error.message); ctx.onError?.(res.error.message); return; }
    setMsg('«' + r.name + '»: контур ' + (nov ? '«' + nov + '»' : 'снят') + (res.data.rabot ? ' · работ: ' + res.data.rabot : ''));
    setRows(cur => cur.map(x => (x.klyuch === r.klyuch ? { ...x, kontur: nov } : x)));
    if (nov && !kontury.includes(nov)) setKontury(k => [...k, nov]);
    ctx.onSaved?.(); ctx.onChanged?.();
  }

  const vsego = rows.filter(r => r.name.trim()).length;
  const rabot = rows.filter((r, i) => r.name.trim() && !estDeti(rows, i)).length;

  return { rows, vidim, noms, svernuto, svernut, kuda, sost, msg, can, vsego, rabot, kontury, logiki, rev, osnovnaya, razbivka,
    pravka, klavisha, vstavka, dragStart, dragEnd, dragOver, dragLeave, drop, smenitRaskatku, smenitDlit, smenitLogiku, smenitKontur, load };
}
export type Ier = ReturnType<typeof useIerarhiya>;

/** ячейка названия: отступ, треугольник раздела, поле ввода */
export function IerImya({ ie, i, placeholder, otkryta, onOtkryt, prosto }: {
  ie: Ier; i: number; placeholder?: string;
  /** у работы в модели: раскрыты ли её задачи и как переключить (плюс/минус, как в таблице) */
  otkryta?: boolean; onOtkryt?: () => void;
  /** только поле ввода, без отступа и треугольника — в таблице модели они свои */
  prosto?: boolean;
}) {
  const r = ie.rows[i];
  const deti = estDeti(ie.rows, i);
  const svern = ie.svernuto.has(r.klyuch);
  const rabota = !deti && !!r.work_id && !!onOtkryt;
  return (
    <>
      {!prosto && <span className="ie-otstup" style={{ width: r.glubina * 22 }} />}
      {!prosto && <span className={'ie-tri' + (deti || rabota ? ' est' : '')}
        title={deti ? (svern ? 'Развернуть этап' : 'Свернуть этап') : rabota ? (otkryta ? 'Свернуть задачи' : 'Показать задачи') : undefined}
        onClick={ev => { ev.stopPropagation(); if (deti) ie.svernut(r.klyuch); else if (rabota) onOtkryt!(); }}>
        {deti ? (svern ? '▸' : '▾') : rabota ? (otkryta ? '−' : '+') : '·'}</span>}
      <input data-i={i} value={r.name} disabled={!ie.can} spellCheck={false}
        placeholder={placeholder ?? (i === ie.rows.length - 1 ? (ie.vsego ? 'следующая строка…' : 'первый этап или работа…') : '')}
        onClick={ev => ev.stopPropagation()}
        onChange={ev => { const v = ev.target.value; ie.pravka(rs => rs.map((x, j) => (j === i ? { ...x, name: v } : x))); }}
        onKeyDown={ev => void ie.klavisha(ev, i)}
        onPaste={ev => ie.vstavka(ev, i)} />
    </>
  );
}

/** ячейки «Дней» и «Раскатка» — только у работы (столбец с числом задач убран владельцем 2026-10-06) */
export function IerPolya({ ie, i, onCard }: { ie: Ier; i: number;
  /** открыть карточку работы — остальные признаки справочника (место, группа, корпуса, календарь, ТК…) */
  onCard?: (name: string) => void }) {
  const r = ie.rows[i];
  const deti = estDeti(ie.rows, i);
  const rabota = !deti && !!r.work_id;
  return (
    <>
      {/* карточка работы — все признаки справочника (владелец 2026-10-16: «назначение признаков из справочника работ») */}
      <td className="ie-kart" onClick={ev => ev.stopPropagation()}>
        {rabota && onCard && <span className="ie-kart-b" title="Карточка работы: все признаки справочника — место, группа, корпуса, календарь, тепловой контур, связи"
          onClick={() => onCard(r.name)}>⋯</span>}
      </td>
      <td className="ie-dlit" onClick={ev => ev.stopPropagation()}>
        {rabota && <input key={r.klyuch + ':' + (r.dlit ?? '')} className="ie-dlit-v" defaultValue={r.dlit ?? ''}
          disabled={!ie.can} placeholder="—" title="Длительность на один этаж или корпус, дней (справочник). Enter — сохранить"
          onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); (ev.target as HTMLInputElement).blur(); } ev.stopPropagation(); }}
          onBlur={ev => void ie.smenitDlit(r, ev.target.value)} />}
      </td>
      <td className="ie-rask" onClick={ev => ev.stopPropagation()}>
        {rabota && <VyborSpiska value={r.raskatka || 'этажи'} opts={Object.keys(RASK_LABEL)} podpisi={RASK_LABEL}
          disabled={!ie.can} className="ie-rask-v" title="Раскатка работы: по каким этажам или корпусам создаются её задачи"
          onPick={v => void ie.smenitRaskatku(r, v)} />}
      </td>
      <td className="ie-por" onClick={ev => ev.stopPropagation()}>
        {rabota && <VyborSpiska value={r.logika || ''} opts={ie.logiki} pusto="— по умолчанию —"
          disabled={!ie.can} className="ie-rask-v" title="Порядок этажей (логика монтажа): в какой очерёдности бригада идёт по этажам. Пусто — «Типовые снизу вверх»"
          onPick={v => void ie.smenitLogiku(r, v)} />}
      </td>
    </>
  );
}

/** подсказка по клавишам и состояние сохранения — в полосе над таблицей */
export function IerPodskazka({ ie }: { ie: Ier }) {
  const sost = ie.sost;
  return (
    <>
      <span className="pw-muted">разделов {ie.vsego - ie.rabot} · работ {ie.rabot}</span>
      <span className={'ie-sost ie-' + sost}>{sost === 'сохраняю' ? 'сохраняю…' : sost === 'сохранено' ? 'сохранено' : sost === 'ошибка' ? 'не сохранилось' : sost === 'правка' ? '…' : ''}</span>
      <span className="pw-muted ie-msg" title={ie.msg}>{ie.msg}</span>
      <span className="grow" />
      <span className="pw-muted" title="Enter — строка ниже; Tab / Shift+Tab — вложить / поднять; Alt+↑↓ — переставить; ↑↓ — ходить; стереть название и Backspace — убрать строку (работа уйдёт из графика с задачами); Ctrl+V — вставить список из Excel, отступ табуляцией = уровень; мышью за номер — перенести. Строка без детей — работа: сразу в справочнике и графике">
        Enter · Tab · Shift+Tab · Alt+↑↓ · Ctrl+V · мышью за номер <span className="spr-ico">?</span></span>
    </>
  );
}

/** Отдельный экран (не в меню; «Модель ГПР» рисует те же строки со своими датами и Гантом) */
export function Ierarhiya({ ctx, onBack }: { ctx: ShellCtx; onBack: () => void }) {
  const koren = useRef<HTMLDivElement>(null);
  const ie = useIerarhiya(ctx, koren);
  return (
    <div className="ie" ref={koren}>
      <div className="bar actbar ie-bar">
        <button className="sec" onClick={onBack}>← К модели</button>
        <b>Иерархия работ</b>
        <IerPodskazka ie={ie} />
      </div>
      <div className="tbl ie-tbl">
        <table>
          <thead><tr><th className="ie-nom">№</th><th className="ie-name">Название</th>
            <th className="ie-dlit">Дней</th><th className="ie-rask">Раскатка</th></tr></thead>
          <tbody>
            {ie.rows.map((r, i) => {
              if (!ie.vidim[i]) return null;
              const deti = estDeti(ie.rows, i);
              const k = ie.kuda;
              return (
                <tr key={r.klyuch} className={'ie-str' + (deti ? ' razdel' : '') + (r.name.trim() ? '' : ' pusto')
                    + (k && k.i === i ? (k.posle ? ' drop-posle' : ' drop-do') : '')}
                  style={k && k.i === i ? { '--drop-otstup': (k.glubina * 22 + 64) + 'px' } as React.CSSProperties : undefined}
                  onDragOver={ev => ie.dragOver(ev, i)} onDragLeave={() => ie.dragLeave(i)}
                  onDrop={ev => { ev.preventDefault(); ie.drop(i); }}>
                  <td className={'ie-nom pw-muted' + (ie.can && r.name.trim() ? ' ie-ruchka' : '')} draggable={ie.can && !!r.name.trim()}
                    onDragStart={ev => ie.dragStart(ev, i)} onDragEnd={ie.dragEnd}>{ie.noms[i]}</td>
                  <td className="ie-name"><IerImya ie={ie} i={i} /></td>
                  <IerPolya ie={ie} i={i} />
                </tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** контур планирования раздела — выпадающий список из «Списков» (владелец 2026-10-07),
    «— без контура —» снимает, «+ новый контур…» спрашивает название; в модели стоит слева от
    названия, в столбце «Контур» */
const NOVYJ_KONTUR = ' новый';
export function IerKontur({ ie, r }: { ie: Ier; r: IerStroka }) {
  const opts = [...ie.kontury];
  if (r.kontur && !opts.includes(r.kontur)) opts.unshift(r.kontur);
  return (
    <VyborSpiska value={r.kontur || ''} opts={[...opts, NOVYJ_KONTUR]} podpisi={{ [NOVYJ_KONTUR]: '+ новый контур…' }}
      pusto="— без контура —" placeholder="контур…" disabled={!ie.can} className="ie-kontur-v"
      title="Контур планирования этапа: проставится всем работам под ним и появится кнопкой в фильтрах сверху"
      onPick={v => {
        if (v === NOVYJ_KONTUR) {
          void ask('Название нового контура планирования:').then(n => { if (n && n.trim()) void ie.smenitKontur(r, n.trim()); });
          return;
        }
        void ie.smenitKontur(r, v);
      }} />
  );
}
